<template>
  <div class="shell-bg min-h-screen text-neo-black">
    <SpeedInsights />

    <!-- Ambient Retro Geometric Accents -->
    <div class="ambient-shape ambient-shape-1" aria-hidden="true"></div>
    <div class="ambient-shape ambient-shape-2" aria-hidden="true"></div>
    <div class="ambient-shape ambient-shape-3" aria-hidden="true"></div>

    <!-- Neo-Brutalist Navigation Bar -->
    <header class="nav-wrapper">
      <nav class="nav-shell">
        <div class="mx-auto flex max-w-7xl items-center justify-between gap-3 px-3 py-3 sm:px-6 lg:px-8">
          <!-- Brand Logo & Badge -->
          <button @click="redirectToHome" class="brand-chip group" aria-label="Go to Home">
            <span class="brand-mark">RS</span>
            <div class="brand-copy">
              <span class="brand-name">Reynaldi Siregar</span>
              <span class="brand-sub">Front-End Developer</span>
            </div>
          </button>

          <!-- Desktop Navigation -->
          <div class="hidden md:flex items-center gap-1.5 lg:gap-2.5" id="desktop-nav">
            <router-link
              v-for="item in navItems"
              :key="item.to"
              :to="item.to"
              class="nav-link"
            >
              {{ item.label }}
            </router-link>
          </div>

          <!-- Quick Action & Mobile Toggle -->
          <div class="flex items-center gap-2.5">
            <a
              href="https://github.com/reysiregar"
              class="hidden md:inline-flex nav-link nav-link-github"
              target="_blank"
              rel="noreferrer"
              aria-label="GitHub Profile"
            >
              <svg class="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v-3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
              GitHub
            </a>

            <button
              @click="toggleMobileMenu"
              class="menu-button md:hidden"
              :class="{ open: isMobileMenuOpen }"
              aria-label="Toggle navigation menu"
            >
              <span class="bar bar-1"></span>
              <span class="bar bar-2"></span>
              <span class="bar bar-3"></span>
            </button>
          </div>
        </div>
      </nav>
    </header>

    <!-- Mobile Drawer & Backdrop -->
    <div 
      class="mobile-overlay md:hidden"
      :class="{ 'active': isMobileMenuOpen }"
      @click="closeMobileMenu"
    ></div>

    <aside 
      class="mobile-drawer md:hidden"
      :class="{ 'open': isMobileMenuOpen }"
      aria-label="Mobile Navigation"
    >
      <div class="flex h-full flex-col justify-between p-5">
        <div>
          <!-- Drawer Header -->
          <div class="flex items-center justify-between border-b-2.5 border-neo-black pb-4">
            <div class="flex items-center gap-2.5">
              <span class="brand-mark small">RS</span>
              <div>
                <p class="text-xs font-mono font-bold tracking-wider uppercase text-gray-600">[ NAVIGATION ]</p>
                <p class="text-base font-display font-black">Reynaldi Siregar</p>
              </div>
            </div>
            <button @click="closeMobileMenu" class="drawer-close" aria-label="Close menu">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Drawer Navigation Links -->
          <nav class="mt-6 flex flex-col gap-2.5">
            <router-link
              v-for="(item, idx) in navItems"
              :key="item.to"
              :to="item.to"
              @click="closeMobileMenu"
              class="drawer-link"
              :style="{ animationDelay: `${idx * 40}ms` }"
            >
              <span class="font-display font-bold">{{ item.label }}</span>
              <span class="drawer-arrow">→</span>
            </router-link>
          </nav>
        </div>

        <!-- Drawer Footer -->
        <div class="border-t-2.5 border-neo-black pt-4">
          <div class="flex items-center justify-between">
            <a 
              href="https://github.com/reysiregar" 
              class="drawer-github-btn"
              target="_blank" 
              rel="noreferrer"
            >
              <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v-3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
              github.com/reysiregar
            </a>
            <span class="font-mono text-xs font-bold text-gray-500">v2.0</span>
          </div>
        </div>
      </div>
    </aside>

    <!-- Main Content Mount Point -->
    <main class="main-content mx-auto w-full max-w-7xl px-4 pb-12 pt-24 sm:px-6 lg:px-8 lg:pt-28">
      <router-view :showContent="true" />
    </main>

    <!-- Clean Neo-Brutalist Footer -->
    <footer class="site-footer">
      <div class="mx-auto flex max-w-7xl flex-col sm:flex-row items-center justify-between gap-4 px-4 py-6 text-center sm:text-left sm:px-6 lg:px-8">
        <div class="flex flex-wrap items-center justify-center sm:justify-start gap-2 font-mono text-xs font-bold text-gray-700">
          <span>© {{ currentYear }} Reynaldi Siregar</span>
          <span>•</span>
          <span>Bekasi, Indonesia</span>
        </div>

        <div class="footer-links-wrap flex flex-wrap items-center justify-center sm:justify-end gap-2.5 sm:gap-3 font-mono text-xs font-bold">
          <a href="https://github.com/reysiregar" target="_blank" rel="noreferrer" class="footer-link">GitHub</a>
          <a href="https://www.linkedin.com/in/reynaldi-siregar-490b8b261" target="_blank" rel="noreferrer" class="footer-link">LinkedIn</a>
          <a href="https://instagram.com/reysiregars" target="_blank" rel="noreferrer" class="footer-link">Instagram</a>
          <a href="https://www.rndkreatif.tech" target="_blank" rel="noreferrer" class="footer-link footer-link-special" aria-label="Visit RND Kreatif">
            <span>RND Kreatif</span>
            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M7 17L17 7M17 7H7M17 7V17"/>
            </svg>
          </a>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { SpeedInsights } from "@vercel/speed-insights/vue";

let scrollTimeout;

export default {
  components: {
    SpeedInsights,
  },
  data() {
    return {
      isMobileMenuOpen: false,
      currentYear: new Date().getFullYear(),
      navItems: [
        { label: 'Home', to: '/' },
        { label: 'About', to: '/about' },
        { label: 'Work', to: '/portfolio' },
        { label: 'Certificates', to: '/certificate' },
        { label: 'Blog', to: '/blog' },
        { label: 'Contact', to: '/contact' },
      ],
    };
  },
  methods: {
    redirectToHome() {
      this.$router.push('/');
    },
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
      document.body.style.overflow = this.isMobileMenuOpen ? 'hidden' : '';
    },
    closeMobileMenu() {
      this.isMobileMenuOpen = false;
      document.body.style.overflow = '';
    },
    handleScroll() {
      document.body.classList.add('scrolling');
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        document.body.classList.remove('scrolling');
      }, 700);
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    document.body.style.overflow = '';
  },
  watch: {
    $route() {
      this.closeMobileMenu();
      window.scrollTo(0, 0);
    }
  }
};
</script>

<style>
:root {
  --neo-bg: #FAF7F0;
  --neo-black: #121212;
  --neo-white: #FFFFFF;
  --neo-cream: #F4EFE6;
  --neo-yellow: #FFE600;
  --neo-yellow-light: #FEF08A;
  --neo-blue: #3B82F6;
  --neo-blue-light: #DBEAFE;
  --neo-mint: #10B981;
  --neo-mint-light: #D1FAE5;
  --neo-coral: #FF5A5F;
  --neo-coral-light: #FFE4E6;
  --neo-purple: #8B5CF6;
  --neo-purple-light: #EDE9FE;
  --neo-pink: #F472B6;
  
  --border-neo: 2.5px solid var(--neo-black);
  --border-neo-thick: 3.5px solid var(--neo-black);
  
  --shadow-xs: 2px 2px 0 var(--neo-black);
  --shadow-sm: 3px 3px 0 var(--neo-black);
  --shadow-md: 5px 5px 0 var(--neo-black);
  --shadow-lg: 8px 8px 0 var(--neo-black);
  --shadow-xl: 12px 12px 0 var(--neo-black);
}

*,
*::before,
*::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  font-family: 'Plus Jakarta Sans', sans-serif;
  background-color: var(--neo-bg);
  color: var(--neo-black);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  overflow-x: hidden;
}

::selection {
  background: var(--neo-yellow);
  color: var(--neo-black);
}

@media (min-width: 769px) {
  html {
    scrollbar-width: thin;
    scrollbar-color: var(--neo-black) var(--neo-bg);
  }

  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--neo-bg);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--neo-black);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--neo-blue);
  }
}

@media (max-width: 768px) {
  html {
    scrollbar-width: none;
  }

  ::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
}

/* Canvas Background Dot Grid Pattern */
.shell-bg {
  position: relative;
  overflow-x: hidden;
  background-color: var(--neo-bg);
  background-image: radial-gradient(rgba(18, 18, 18, 0.12) 1.25px, transparent 1.25px);
  background-size: 24px 24px;
}

/* Ambient Neo-Brutalist Background Shapes */
.ambient-shape {
  position: fixed;
  pointer-events: none;
  border: var(--border-neo);
  opacity: 0.16;
  z-index: 0;
}

@media (max-width: 768px) {
  .ambient-shape {
    display: none;
  }
}

.ambient-shape-1 {
  width: 220px;
  height: 220px;
  top: 15%;
  left: -60px;
  background: var(--neo-yellow);
  box-shadow: var(--shadow-lg);
  transform: rotate(-12deg);
  border-radius: 24px;
}

.ambient-shape-2 {
  width: 280px;
  height: 280px;
  bottom: 20%;
  right: -90px;
  background: var(--neo-blue);
  box-shadow: var(--shadow-lg);
  transform: rotate(15deg);
  border-radius: 32px;
}

.ambient-shape-3 {
  width: 140px;
  height: 140px;
  top: 60%;
  left: 5%;
  background: var(--neo-coral);
  box-shadow: var(--shadow-md);
  transform: rotate(8deg);
  border-radius: 18px;
}

/* Header & Floating Navigation */
.nav-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 90;
  padding: 0.75rem 1rem 0;
}

.nav-shell {
  max-width: 1280px;
  margin: 0 auto;
  border: var(--border-neo);
  background: rgba(250, 247, 240, 0.94);
  backdrop-filter: blur(14px);
  border-radius: 16px;
  transition: all 0.2s ease;
}

/* Brand Chip */
.brand-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.45rem 0.85rem 0.45rem 0.5rem;
  border: var(--border-neo);
  background: var(--neo-white);
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  text-align: left;
}

.brand-chip:hover {
  background: var(--neo-yellow-light);
}

.brand-mark {
  display: grid;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  border: 2px solid var(--neo-black);
  background: var(--neo-yellow);
  color: var(--neo-black);
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 900;
  font-size: 0.95rem;
  border-radius: 8px;
}

.brand-mark.small {
  width: 2rem;
  height: 2rem;
  font-size: 0.85rem;
}

.brand-copy {
  display: flex;
  flex-direction: column;
}

.brand-name {
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 800;
  font-size: 0.95rem;
  letter-spacing: -0.02em;
  color: var(--neo-black);
  line-height: 1.1;
}

.brand-sub {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.68rem;
  font-weight: 600;
  color: #4B5563;
  line-height: 1.2;
}

/* Desktop Navigation Links */
.nav-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 0.9rem;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: 0.88rem;
  letter-spacing: -0.01em;
  color: var(--neo-black);
  border: 2px solid transparent;
  border-radius: 10px;
  text-decoration: none;
  transition: all 0.15s ease;
}

.nav-link:hover {
  border-color: var(--neo-black);
  background: var(--neo-white);
}

.nav-link.router-link-exact-active,
.nav-link.router-link-active {
  border: 2px solid var(--neo-black);
  background: var(--neo-yellow);
  color: var(--neo-black);
  font-weight: 800;
}

.nav-link-github {
  border: var(--border-neo);
  background: var(--neo-white);
}

.nav-link-github:hover {
  background: var(--neo-black);
  color: var(--neo-white);
}

/* Menu Toggle Button */
.menu-button {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  width: 2.6rem;
  height: 2.6rem;
  border: var(--border-neo);
  background: var(--neo-white);
  border-radius: 10px;
  box-shadow: 2px 2px 0 var(--neo-black);
  transform: translate(0, 0);
  cursor: pointer;
  transition: all 0.12s cubic-bezier(0.16, 1, 0.3, 1);
}

.menu-button:hover {
  background: var(--neo-yellow);
  transform: translate(1px, 1px);
  box-shadow: 1px 1px 0 var(--neo-black);
}

.menu-button:active {
  background: var(--neo-yellow);
  transform: translate(2px, 2px);
  box-shadow: 0px 0px 0 var(--neo-black);
}

.menu-button .bar {
  display: block;
  width: 18px;
  height: 2.5px;
  background: var(--neo-black);
  border-radius: 2px;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.menu-button.open .bar-1 {
  transform: translateY(6.5px) rotate(45deg);
}

.menu-button.open .bar-2 {
  opacity: 0;
}

.menu-button.open .bar-3 {
  transform: translateY(-6.5px) rotate(-45deg);
}

/* Mobile Drawer Overlay */
.mobile-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(18, 18, 18, 0.45);
  backdrop-filter: blur(4px);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s ease;
}

.mobile-overlay.active {
  opacity: 1;
  pointer-events: auto;
}

/* Mobile Drawer Sidebar */
.mobile-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 110;
  width: min(22rem, 86vw);
  background: var(--neo-bg);
  border-left: var(--border-neo);
  transform: translateX(100%);
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.mobile-drawer.open {
  transform: translateX(0);
}

.drawer-close {
  display: grid;
  place-items: center;
  width: 2.2rem;
  height: 2.2rem;
  border: 2px solid var(--neo-black);
  background: var(--neo-coral);
  color: var(--neo-white);
  font-weight: 900;
  font-size: 1rem;
  border-radius: 8px;
  box-shadow: 2px 2px 0 var(--neo-black);
  transform: translate(0, 0);
  cursor: pointer;
  transition: all 0.12s cubic-bezier(0.16, 1, 0.3, 1);
}

.drawer-close:hover {
  transform: translate(1px, 1px);
  box-shadow: 1px 1px 0 var(--neo-black);
}

.drawer-close:active {
  transform: translate(2px, 2px);
  box-shadow: 0px 0px 0 var(--neo-black);
}

.drawer-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border: var(--border-neo);
  background: var(--neo-white);
  box-shadow: 3px 3px 0 var(--neo-black);
  border-radius: 12px;
  font-size: 0.98rem;
  text-decoration: none;
  color: var(--neo-black);
  transform: translate(0, 0);
  transition: all 0.12s cubic-bezier(0.16, 1, 0.3, 1);
}

.drawer-link:hover {
  background: var(--neo-yellow);
  transform: translate(1.5px, 1.5px);
  box-shadow: 1.5px 1.5px 0 var(--neo-black);
}

.drawer-link:active {
  background: var(--neo-yellow);
  transform: translate(3px, 3px);
  box-shadow: 0px 0px 0 var(--neo-black);
}

.drawer-link.router-link-exact-active,
.drawer-link.router-link-active {
  background: var(--neo-yellow);
  font-weight: 800;
}

.drawer-arrow {
  font-weight: 900;
  font-size: 1.1rem;
}

.drawer-github-btn {
  display: inline-flex;
  align-items: center;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--neo-black);
  text-decoration: none;
}

.drawer-github-btn:hover {
  text-decoration: underline;
}

/* Site Footer */
.site-footer {
  position: relative;
  z-index: 10;
  border-top: var(--border-neo);
  background: rgba(250, 247, 240, 0.95);
}

.footer-link {
  color: var(--neo-black);
  text-decoration: none;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.footer-link:hover {
  background: var(--neo-white);
  box-shadow: var(--shadow-xs);
  border: 1.5px solid var(--neo-black);
}

.footer-link-special {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.65rem;
  background: var(--neo-yellow);
  color: var(--neo-black);
  border: 1.5px solid var(--neo-black);
  border-radius: 8px;
  box-shadow: 2px 2px 0 var(--neo-black);
  transform: translate(0, 0);
  transition: all 0.12s cubic-bezier(0.16, 1, 0.3, 1);
  font-weight: 800;
  text-decoration: none;
}

.footer-link-special:hover {
  background: var(--neo-yellow);
  transform: translate(1px, 1px);
  box-shadow: 1px 1px 0 var(--neo-black);
}

.footer-link-special:active {
  transform: translate(2px, 2px);
  box-shadow: 0px 0px 0 var(--neo-black);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .nav-wrapper {
    padding: 0.5rem 0.65rem 0;
  }

  .nav-shell {
    border-radius: 12px;
  }

  .brand-sub {
    display: none;
  }

  .brand-name {
    font-size: 0.88rem;
  }

  .footer-links-wrap {
    width: 100%;
    justify-content: center;
    gap: 0.5rem;
  }

  .footer-link {
    padding: 0.35rem 0.55rem;
    font-size: 0.72rem;
  }

  .footer-link-special {
    padding: 0.4rem 0.85rem;
    font-size: 0.75rem;
    box-shadow: 2.5px 2.5px 0 var(--neo-black);
  }
}
</style>